import { min } from 'lib/array/arrayUtils'
import { EXPERIENCE_TRANSFER_ID } from 'constants/experience'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'

const MAX_KM_FROM_HOTEL = 100

/**
 * Pure per-order variant of the `getOrderLoyaltyTransferExperience` selector — finds the best
 * loyalty transfer experience near the order's property. Use when the order is not the
 * route-viewed order (e.g. the upcoming-escapes card).
 */
export default function determineOrderLoyaltyTransferExperience(
  order: App.Order | undefined,
  experienceLists: App.State['experience']['experienceLists'],
  experiences: App.State['experience']['experiences'],
) {
  if (!order) return undefined

  const property = order.items[0]?.offer?.property
  if (!property) return undefined

  const allExperiences = Object.values(experienceLists)
    .flatMap(list => list?.ids ?? [])
    .map(id => experiences[id])
    .filter(Boolean)

  const bookingViews = allExperiences.map(exp =>
    mapExperienceToBookingView(exp, undefined, property as unknown as App.OfferProperty),
  )

  const transfers = bookingViews.filter(view =>
    view.categories.some(cat => cat.id === EXPERIENCE_TRANSFER_ID) &&
    !!view.features?.transfer &&
    !!view.distance &&
    view.distance <= MAX_KM_FROM_HOTEL &&
    view.price > 0,
  )

  if (!transfers.length) return undefined

  const preferred = transfers.filter(t => !!t.features?.transfer?.preferredTransfer)
  return preferred.length ? min(preferred, t => t.price) : min(transfers, t => t.price)
}

export type LoyaltyTransferExperience = ReturnType<typeof determineOrderLoyaltyTransferExperience>
